<template>
  <div v-if="items.length > 0" class="pdf-parse-queue mb-4">
    <h6
      class="queue-header d-flex align-items-center justify-content-between"
      role="button"
      tabindex="0"
      @click="collapsed = !collapsed"
      @keydown.enter="collapsed = !collapsed"
      @keydown.space.prevent="collapsed = !collapsed"
    >
      <span>
        <i class="bi me-1" :class="collapsed ? 'bi-chevron-right' : 'bi-chevron-down'"></i>
        <i class="bi bi-list-task me-1"></i>Parse-wachtrij
      </span>
      <span class="d-flex align-items-center gap-2">
        <button
          v-if="isAdmin && finishedCount > 0"
          class="btn btn-sm btn-outline-secondary py-0"
          :disabled="clearing"
          title="Alle afgehandelde (klaar/gefaald) orders uit de wachtrij verwijderen"
          @click.stop="clearFinished"
          @keydown.enter.stop
          @keydown.space.stop
        >
          <span v-if="clearing" class="spinner-border spinner-border-sm" role="status"></span>
          <i v-else class="bi bi-trash me-1"></i>Afgehandelde opruimen ({{ finishedCount }})
        </button>
        <span class="badge bg-secondary">{{ activeCount }} bezig/wachtend</span>
      </span>
    </h6>

    <div v-show="!collapsed" class="queue-items mt-2">
      <div
        v-if="displayedItems.length === 0"
        class="text-muted small text-center py-2"
      >
        Geen orders van jou in verwerking.
      </div>
      <div
        v-for="item in displayedItems"
        :key="item.id"
        class="card mb-2"
        :class="borderClass(item.status)"
      >
        <div class="card-body p-2">
          <div class="d-flex justify-content-between align-items-start">
            <div class="text-truncate me-2">
              <small class="fw-bold d-block text-truncate" :title="item.original_filename">
                {{ item.original_filename }}
              </small>
              <small
                v-if="isAdmin"
                class="text-muted d-block text-truncate"
                :title="item.uploaded_by_email"
              >
                <i class="bi bi-person me-1"></i>{{ item.uploaded_by_email }}
              </small>
            </div>
            <span class="badge flex-shrink-0" :class="badgeClass(item.status)">
              <span
                v-if="item.status === 'processing'"
                class="spinner-border spinner-border-sm me-1"
                role="status"
                style="width: 0.7rem; height: 0.7rem;"
              ></span>
              {{ statusLabel(item.status) }}
            </span>
          </div>

          <div v-if="item.status === 'failed' && item.error_message" class="mt-1">
            <small class="text-danger d-block text-truncate" :title="item.error_message">
              {{ item.error_message }}
            </small>
          </div>

          <div class="d-flex justify-content-end gap-2 mt-2">
            <button
              v-if="item.status === 'done' && item.order_id"
              class="btn btn-sm btn-primary"
              @click="goToOrderDetail(item.order_id)"
            >
              <i class="bi bi-arrow-right"></i> Bekijk order
            </button>
            <button
              v-if="item.status === 'failed'"
              class="btn btn-sm btn-outline-primary"
              :disabled="busyId === item.id"
              @click="retry(item)"
            >
              <span v-if="busyId === item.id" class="spinner-border spinner-border-sm" role="status"></span>
              <i v-else class="bi bi-arrow-clockwise"></i> Opnieuw
            </button>
            <button
              v-if="item.status === 'done' || item.status === 'failed'"
              class="btn btn-sm btn-outline-danger"
              :disabled="busyId === item.id"
              @click="remove(item)"
            >
              <i class="bi bi-trash"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ServiceFactory from '../services/ServiceFactory';
import localDB from '../utils/localDB';

export default {
  name: 'PdfParseQueueList',
  props: {
    isAdmin: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      items: [],
      queueService: null,
      webSocketService: null,
      busyId: null,
      clearing: false,
      collapsed: true,
      userEmail: '',
    };
  },
  computed: {
    // Globale teller (bezig/wachtend) - voor iedereen hetzelfde.
    activeCount() {
      return this.items.filter((i) => i.status === 'queued' || i.status === 'processing').length;
    },
    // Aantal afgehandelde items (klaar/gefaald) - basis voor de admin-opschoonknop.
    finishedCount() {
      return this.items.filter((i) => i.status === 'done' || i.status === 'failed').length;
    },
    // Admins zien de volledige lijst (incl. klaar/gefaald, alle medewerkers).
    // PDF-parsers zien alleen hun eigen items en niet de afgeronde (klaar) orders,
    // zodat hun stack leeg is zodra hun werk gedaan is.
    displayedItems() {
      if (this.isAdmin) {
        return this.items;
      }
      return this.items.filter(
        (i) => i.uploaded_by_email === this.userEmail && i.status !== 'done'
      );
    },
  },
  async mounted() {
    this.queueService = ServiceFactory.getService('PdfParseQueueService');
    this.webSocketService = ServiceFactory.getService('WebSocketService');

    const token = await localDB.getItem('token');
    this.userEmail = this.decodeEmail(token);

    // Live updates van de gedeelde wachtrij.
    this.webSocketService.addEventListener('parseQueueUpdated', this.handleQueueUpdated);
    this.webSocketService.connect();

    await this.loadQueue();
  },
  beforeUnmount() {
    if (this.webSocketService) {
      this.webSocketService.removeEventListener('parseQueueUpdated', this.handleQueueUpdated);
    }
  },
  methods: {
    decodeEmail(token) {
      if (!token || typeof token !== 'string') return '';
      try {
        const part = token.split('.')[1];
        if (!part) return '';
        const padded = part.replace(/-/g, '+').replace(/_/g, '/');
        const json = atob(padded + '==='.slice((padded.length + 3) % 4));
        const payload = JSON.parse(decodeURIComponent(escape(json)));
        return (payload.email || payload.preferred_username || payload.upn || '').toLowerCase();
      } catch (e) {
        return '';
      }
    },
    async loadQueue() {
      try {
        const result = await this.queueService.getQueue();
        if (result.success) {
          this.items = result.data;
        }
      } catch (error) {
        console.error('Fout bij laden parse-wachtrij:', error);
      }
    },
    handleQueueUpdated(event) {
      this.items = event.detail || [];
    },
    async retry(item) {
      this.busyId = item.id;
      try {
        await this.queueService.retry(item.id);
      } catch (error) {
        this.$emit('showToast', ['Opnieuw proberen mislukt', 'bg-danger']);
      } finally {
        this.busyId = null;
      }
    },
    async remove(item) {
      this.busyId = item.id;
      try {
        await this.queueService.remove(item.id);
      } catch (error) {
        this.$emit('showToast', ['Verwijderen mislukt', 'bg-danger']);
      } finally {
        this.busyId = null;
      }
    },
    async clearFinished() {
      this.clearing = true;
      try {
        await this.queueService.removeFinished();
        // De wachtrij wordt via WebSocket ('parseQueueUpdated') automatisch bijgewerkt.
      } catch (error) {
        this.$emit('showToast', ['Opruimen mislukt', 'bg-danger']);
      } finally {
        this.clearing = false;
      }
    },
    goToOrderDetail(orderId) {
      this.$router.push(`/orderDetail/${orderId}`);
    },
    statusLabel(status) {
      return {
        queued: 'In wachtrij',
        processing: 'Bezig',
        done: 'Klaar',
        failed: 'Gefaald',
      }[status] || status;
    },
    badgeClass(status) {
      return {
        queued: 'bg-secondary',
        processing: 'bg-info',
        done: 'bg-success',
        failed: 'bg-danger',
      }[status] || 'bg-secondary';
    },
    borderClass(status) {
      return {
        queued: 'border-secondary',
        processing: 'border-info',
        done: 'border-success',
        failed: 'border-danger',
      }[status] || '';
    },
  },
};
</script>

<style scoped>
.queue-header {
  cursor: pointer;
  user-select: none;
}
.queue-items {
  max-height: 400px;
  overflow-y: auto;
}
</style>
